import {
  Component,
  Input,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTagModule } from 'ng-zorro-antd/tag';

@Component({
  selector: 'wilson-route-renderer',
  template: `
    <nz-form-item class="no-margin">
      @if (isEditable) {
        <nz-form-control class="no-margin">
          <nz-select
            class="mb-2"
            [nzDisabled]="disabled"
            [nzPlaceHolder]="'syncLogic.startLocation' | translate"
            [(ngModel)]="startLocation"
            (ngModelChange)="onChangeStartLocation($event)"
            [nzPlacement]="'bottomRight'"
            [nzDropdownStyle]="{ 'min-width': '400px' }"
            [nzDropdownMatchSelectWidth]="false"
          >
            @for (route of routes; track route) {
              <nz-option [nzLabel]="route" [nzValue]="route"> </nz-option>
            }
          </nz-select>
        </nz-form-control>
        <nz-form-control class="no-margin">
          <nz-select
            [nzPlaceHolder]="'syncLogic.endLocation' | translate"
            [nzDisabled]="disabled"
            [(ngModel)]="endLocation"
            (ngModelChange)="onChangeEndLocation($event)"
            [nzPlacement]="'bottomRight'"
            [nzDropdownStyle]="{ 'min-width': '400px' }"
            [nzDropdownMatchSelectWidth]="false"
          >
            @for (route of filteredEndRoutes; track route) {
              <nz-option [nzLabel]="route" [nzValue]="route"> </nz-option>
            }
          </nz-select>
        </nz-form-control>
      } @else {
        <div class="route-row">
          <nz-tag nzColor="default" class="mx-1 route-tag">
            <span
              class="text-display"
              nz-tooltip
              [nzTooltipTitle]="routeString"
            >
              {{ routeString }}
            </span>
          </nz-tag>
        </div>
      }
    </nz-form-item>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .route-row {
        display: flex;
        align-items: center;
      }

      .route-tag {
        width: 150px;
        max-width: 150px;
        cursor: default;
      }

      .route-tag ::ng-deep .ant-tag {
        width: 100%;
        max-width: 150px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .text-display {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
        display: block;
      }
    `,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    NzSelectModule,
    TranslatePipe,
    NzFormModule,
    NzTooltipModule,
    FontAwesomeModule,
    NzInputModule,
    NzTagModule,
  ],
})
export class RouteRendererComponent implements OnInit, OnChanges {
  private cdr = inject(ChangeDetectorRef);

  @Input() routes!: string[];
  @Input() disabled = false;
  @Input() isEditable = false;
  @Input() routeArray: string[] = [];
  @Output() startLocationChanged = new EventEmitter<string>();
  @Output() endLocationChanged = new EventEmitter<string>();

  startLocation = '';
  endLocation = '';
  viaLocations: string[] = [];
  filteredEndRoutes: string[] = [];
  selectedRoutes: string[] = [];

  get routeString(): string {
    if (this.routeArray && this.routeArray.length > 0) {
      return this.routeArray.join(' - ');
    }

    const { startLocation, viaLocations, endLocation } = this;
    if (!startLocation || !endLocation) return '';

    const via = viaLocations?.length ? ` - ${viaLocations.join(' - ')}` : '';
    return `${startLocation}${via} - ${endLocation}`;
  }

  ngOnInit(): void {
    this.initializeFilteredEndRoutes();
  }

  ngOnChanges(): void {
    this.initializeFilteredEndRoutes();
    this.initializeFromRouteArray();
  }

  private initializeFromRouteArray(): void {
    if (this.routeArray && this.routeArray.length > 0) {
      this.startLocation = this.routeArray[0] || '';
      this.endLocation = this.routeArray[this.routeArray.length - 1] || '';
      this.viaLocations = this.routeArray.slice(1, -1);
    }
  }

  private initializeFilteredEndRoutes(): void {
    if (this.startLocation && this.routes) {
      const startIndex = this.routes.indexOf(this.startLocation);
      if (startIndex !== -1) {
        this.filteredEndRoutes = this.routes.slice(startIndex + 1);
      } else {
        this.filteredEndRoutes = [...this.routes];
      }
    } else {
      this.filteredEndRoutes = [...(this.routes || [])];
    }
  }

  async onChangeStartLocation(startRoute: string): Promise<void> {
    this.startLocation = startRoute;
    this.startLocationChanged.emit(startRoute);
    this.initializeFilteredEndRoutes();
    this.cdr.detectChanges();
  }

  async onChangeEndLocation(endRoute: string): Promise<void> {
    this.endLocation = endRoute;
    this.viaLocations = this.routes.slice(
      this.routes.indexOf(this.startLocation) + 1,
      this.routes.indexOf(endRoute),
    );
    this.endLocationChanged.emit(endRoute);
    this.cdr.detectChanges();
  }
}
